/**
 * Calendar Sync Panel Component
 * Displays calendar integration status and sync controls
 */

import { useEffect, useState } from "react";
import { Calendar as CalendarIcon, CheckCircle2, ExternalLink, XCircle, RefreshCw, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";

import {
  getCalendarIntegrations,
  startGoogleOAuth,
  listGoogleCalendars,
  selectGoogleCalendar,
  connectAppleCalendar,
  runCalendarSync,
  disconnectCalendarIntegration,
  type CalendarIntegration,
  type GoogleCalendarInfo,
} from "@/api/calendarSync";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ToastProvider";

export function CalendarSyncPanel() {
  const { push } = useToast();
  const [integrations, setIntegrations] = useState<CalendarIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);

  // Google OAuth flow
  const [showGoogleCalendarPicker, setShowGoogleCalendarPicker] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendarInfo[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>("");

  // ICS flow (works for Apple and shared Google links)
  const [showIcsDialog, setShowIcsDialog] = useState(false);
  const [icsDialogProvider, setIcsDialogProvider] = useState<"apple" | "google">("apple");
  const [icsUrl, setIcsUrl] = useState("");
  const [icsFile, setIcsFile] = useState<File | null>(null);
  const [icsConnecting, setIcsConnecting] = useState(false);
  const [icsError, setIcsError] = useState<string | null>(null);

  const showToast = (message: string, tone: "success" | "error" | "warning" | "info") => {
    const titleMap = {
      success: "Success",
      error: "Error",
      warning: "Warning",
      info: "Info",
    };
    push({
      title: titleMap[tone],
      message,
      variant: tone === "error" ? "destructive" : "default",
    });
  };

  const normalizeIcsUrl = (value: string, provider: "apple" | "google") => {
    const trimmed = value.trim();
    if (!trimmed || provider !== "google") return trimmed;

    try {
      const url = new URL(trimmed);
      if (url.hostname.includes("calendar.google.com") && url.pathname.startsWith("/calendar/embed")) {
        const src = url.searchParams.get("src");
        if (src) {
          return `https://calendar.google.com/calendar/ical/${encodeURIComponent(src)}/public/basic.ics`;
        }
      }
    } catch {
      // keep original if it isn't a parseable URL
    }

    return trimmed;
  };

  const openIcsDialog = (provider: "apple" | "google") => {
    setIcsDialogProvider(provider);
    setIcsUrl("");
    setIcsFile(null);
    setIcsError(null);
    setShowIcsDialog(true);
  };

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      const data = await getCalendarIntegrations();
      setIntegrations(data);
    } catch (error) {
      console.error("Failed to load integrations:", error);
      showToast("Failed to load calendar integrations", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const { auth_url } = await startGoogleOAuth();
      // Redirect to Google OAuth
      window.location.href = auth_url;
    } catch (error) {
      console.error("Failed to start Google OAuth:", error);
      showToast(
        "Google OAuth is not configured yet — paste a shared calendar link instead.",
        "info"
      );
      openIcsDialog("google");
    }
  };

  const handleGoogleCalendarSelection = async () => {
    if (!selectedCalendarId) {
      showToast("Please select a calendar", "warning");
      return;
    }

    try {
      await selectGoogleCalendar(selectedCalendarId);
      showToast("Calendar selected successfully", "success");
      setShowGoogleCalendarPicker(false);
      loadIntegrations();
    } catch (error) {
      console.error("Failed to select calendar:", error);
      showToast("Failed to select calendar", "error");
    }
  };

  const handleSelectCalendar = async () => {
    try {
      const calendars = await listGoogleCalendars();
      setGoogleCalendars(calendars);
      setShowGoogleCalendarPicker(true);

      // Pre-select primary calendar
      const primary = calendars.find((cal) => cal.primary);
      if (primary) {
        setSelectedCalendarId(primary.id);
      }
    } catch (error) {
      console.error("Failed to list calendars:", error);
      showToast("Failed to list calendars", "error");
    }
  };

  const handleConnectIcs = async () => {
    if (!icsUrl && !icsFile) {
      showToast("Please provide an ICS URL or upload a file", "warning");
      return;
    }

    setIcsConnecting(true);
    setIcsError(null);
    try {
      const normalizedUrl = icsUrl ? normalizeIcsUrl(icsUrl, icsDialogProvider) : undefined;
      await connectAppleCalendar(normalizedUrl || undefined, icsFile || undefined);
      const friendlyLabel = icsDialogProvider === "google" ? "Shared calendar" : "Apple Calendar";
      showToast(`${friendlyLabel} connected successfully`, "success");
      setShowIcsDialog(false);
      setIcsUrl("");
      setIcsFile(null);
      loadIntegrations();
    } catch (error) {
      console.error("Failed to connect Apple Calendar:", error);
      const message = error instanceof Error ? error.message : "Failed to connect calendar via ICS";
      setIcsError(message);
      showToast(message, "error");
    } finally {
      setIcsConnecting(false);
    }
  };

  const handleSync = async (provider: "google" | "apple") => {
    setSyncing(provider);
    try {
      const result = await runCalendarSync(provider);
      showToast(`Synced ${result.events_synced} events from ${provider}`, "success");
      loadIntegrations();
    } catch (error) {
      console.error(`Failed to sync ${provider}:`, error);
      showToast(`Failed to sync ${provider} calendar`, "error");
    } finally {
      setSyncing(null);
    }
  };

  const handleDisconnect = async (integrationId: string, provider: string) => {
    if (!confirm(`Disconnect ${provider} calendar?`)) {
      return;
    }

    try {
      await disconnectCalendarIntegration(integrationId);
      showToast(`${provider} calendar disconnected`, "success");
      loadIntegrations();
    } catch (error) {
      console.error("Failed to disconnect:", error);
      showToast("Failed to disconnect calendar", "error");
    }
  };

  const googleIntegration = integrations.find((i) => i.provider === "google");
  const appleIntegration = integrations.find((i) => i.provider === "apple");

  const isGoogleIcsDialog = icsDialogProvider === "google";
  const icsDialogTitle = isGoogleIcsDialog
    ? "Connect shared calendar link"
    : "Connect Apple Calendar";
  const icsDialogDescription = isGoogleIcsDialog
    ? "Paste the shared calendar's ICS URL (webcal:// or https://) to pull those events into Americana."
    : "Provide an ICS subscription URL or upload an .ics file to import your events.";
  const icsUrlHelper = isGoogleIcsDialog
    ? "Copy the shared calendar's ICS link from Google Calendar → Settings and sharing."
    : "Find this in Calendar.app → Preferences → Accounts → iCloud.";

  const getStatusBadge = (status: string) => {
    const variants = {
      connected: { variant: "default" as const, icon: CheckCircle2, color: "text-green-600" },
      error: { variant: "destructive" as const, icon: XCircle, color: "text-red-600" },
      disconnected: { variant: "secondary" as const, icon: XCircle, color: "text-muted-foreground/70" },
    };

    const config = variants[status as keyof typeof variants] || variants.disconnected;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card className="border-border/80 bg-background/70 p-6 text-foreground">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading calendar integrations...</span>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-border/80 bg-background/70 p-6 text-foreground">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Calendar Sync
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your calendar is the source of truth. Sync shows here and see Bandsintown links when matched.
            </p>
          </div>
        </div>

        {/* Google Calendar Integration */}
        <div className="space-y-4">
          <div className="rounded-lg border border-border/70 bg-background/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                  <CalendarIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">Google Calendar</h3>
                  <p className="text-xs text-muted-foreground">
                    {googleIntegration?.calendar_id
                      ? `Calendar: ${googleIntegration.calendar_id}`
                      : "Two-way sync with your Google Calendar"}
                  </p>
                </div>
              </div>
              {googleIntegration && getStatusBadge(googleIntegration.status)}
            </div>

            {googleIntegration ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Last synced:</span>
                  <span>
                    {googleIntegration.last_synced_at
                      ? format(new Date(googleIntegration.last_synced_at), "PPp")
                      : "Never"}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSync("google")}
                    disabled={syncing === "google"}
                  >
                    {syncing === "google" ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Sync Now
                      </>
                    )}
                  </Button>

                  {!googleIntegration.calendar_id && (
                    <Button size="sm" variant="outline" onClick={handleSelectCalendar}>
                      Select Calendar
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDisconnect(googleIntegration.id, "Google")}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-left">
                <Button onClick={handleConnectGoogle} className="w-full">
                  <Plus className="mr-2 h-4 w-4" />
                  Connect Google Calendar
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => openIcsDialog("google")}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Paste shared calendar link
                </Button>
                <p className="text-xs text-muted-foreground">
                  Shared calendars (ICS) mirror events while Google OAuth is pending.
                </p>
              </div>
            )}
          </div>

          {/* Apple Calendar Integration */}
          <div className="rounded-lg border border-border/70 bg-background/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                  <CalendarIcon className="h-5 w-5 text-muted-foreground/70" />
                </div>
                <div>
                  <h3 className="font-medium">Apple Calendar</h3>
                  <p className="text-xs text-muted-foreground">Import via ICS URL or file upload</p>
                </div>
              </div>
              {appleIntegration && getStatusBadge(appleIntegration.status)}
            </div>

            {appleIntegration ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Last synced:</span>
                  <span>
                    {appleIntegration.last_synced_at
                      ? format(new Date(appleIntegration.last_synced_at), "PPp")
                      : "Never"}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSync("apple")}
                    disabled={syncing === "apple"}
                  >
                    {syncing === "apple" ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Sync Now
                      </>
                    )}
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDisconnect(appleIntegration.id, "Apple")}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Disconnect
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => openIcsDialog("apple")} variant="outline" className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Connect Apple Calendar
              </Button>
            )}
          </div>
        </div>

        {integrations.length === 0 && (
          <p className="text-center text-sm text-muted-foreground mt-6">
            No calendars connected yet. Connect Google or Apple Calendar to get started.
          </p>
        )}
      </Card>

      {/* Google Calendar Picker Dialog */}
      <Dialog open={showGoogleCalendarPicker} onOpenChange={setShowGoogleCalendarPicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Google Calendar</DialogTitle>
            <DialogDescription>Choose which calendar you want to sync with Americana.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="calendar-select">Calendar</Label>
              <Select value={selectedCalendarId} onValueChange={setSelectedCalendarId}>
                <SelectTrigger id="calendar-select">
                  <SelectValue placeholder="Select a calendar" />
                </SelectTrigger>
                <SelectContent>
                  {googleCalendars.map((cal) => (
                    <SelectItem key={cal.id} value={cal.id}>
                      {cal.summary} {cal.primary && "(Primary)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoogleCalendarPicker(false)}>
              Cancel
            </Button>
            <Button onClick={handleGoogleCalendarSelection}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apple ICS Connect Dialog */}
      <Dialog open={showIcsDialog} onOpenChange={setShowIcsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{icsDialogTitle}</DialogTitle>
            <DialogDescription>{icsDialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {icsError && (
              <div
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                role="alert"
                aria-live="polite"
              >
                <div className="font-semibold">Connection failed</div>
                <div>{icsError}</div>
                {icsDialogProvider === "google" && icsUrl.includes("calendar.google.com/calendar/embed") && (
                  <div className="mt-1 text-[11px] text-destructive/80">
                    Google embed links are not ICS feeds. Make the calendar public and use its ICS link, or connect
                    with Google OAuth.
                  </div>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="ics-url">ICS Subscription URL</Label>
              <Input
                id="ics-url"
                type="url"
                placeholder="webcal://example.com/calendar.ics"
                value={icsUrl}
                onChange={(e) => setIcsUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">{icsUrlHelper}</p>
            </div>

            <div className="text-center text-sm text-muted-foreground">OR</div>

            <div>
              <Label htmlFor="ics-file">Upload .ics File</Label>
              <Input
                id="ics-file"
                type="file"
                accept=".ics"
                onChange={(e) => setIcsFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIcsDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConnectIcs}
              disabled={icsConnecting || (!icsUrl && !icsFile)}
            >
              {icsConnecting ? "Connecting..." : "Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
